import { useState, useEffect } from "react"
import { useAddNewUserMutation } from "./usersApiSlice"
import { useNavigate } from "react-router-dom"
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave } from "@fortawesome/free-solid-svg-icons"
import { ROLES } from "../../config/roles"
import useTitle from "../../hooks/useTitle"
import { Button, Spinner, Form } from 'react-bootstrap'; // ✅ Form इम्पोर्ट करें

const USER_REGEX = /^[A-z]{3,20}$/
const PWD_REGEX = /^[A-z0-9!@#$%]{4,12}$/
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // ✅ ईमेल वैलिडेशन के लिए
const MOBILE_REGEX = /^[6789][0-9]{9}$/; // ✅ मोबाइल नंबर वैलिडेशन के लिए

const NewUserForm = () => {
    useTitle('techNotes: New User')

    const [addNewUser, {
        isLoading,
        isSuccess,
        isError,
        error
    }] = useAddNewUserMutation()

    const navigate = useNavigate()

    const [username, setUsername] = useState('')
    const [validUsername, setValidUsername] = useState(false)
    const [email, setEmail] = useState('') // ✅ ईमेल स्टेट
    const [validEmail, setValidEmail] = useState(false) // ✅ ईमेल वैलिडेशन
    const [mobile, setMobile] = useState('') // ✅ मोबाइल स्टेट
    const [validMobile, setValidMobile] = useState(false) // ✅ मोबाइल वैलिडेशन
    const [password, setPassword] = useState('')
    const [validPassword, setValidPassword] = useState(false)
    const [roles, setRoles] = useState(["USER"]) // ✅ डिफ़ॉल्ट भूमिका 'USER'
    // ✅ नए सदस्यता (Membership) फ़ील्ड्स
    const [membershipTier, setMembershipTier] = useState('NONE');
    const [membershipStatus, setMembershipStatus] = useState('INACTIVE');
    const [isSponsoredMember, setIsSponsoredMember] = useState(false);


    useEffect(() => {
        setValidUsername(USER_REGEX.test(username))
    }, [username])

    useEffect(() => {
        setValidEmail(EMAIL_REGEX.test(email) || email === ''); // खाली होने पर वैध
    }, [email])

    useEffect(() => {
        setValidMobile(MOBILE_REGEX.test(mobile) || mobile === ''); // खाली होने पर वैध
    }, [mobile])

    useEffect(() => {
        setValidPassword(PWD_REGEX.test(password))
    }, [password])

    useEffect(() => {
        if (isSuccess) {
            setUsername('')
            setEmail('') // ✅ ईमेल रीसेट करें
            setMobile('') // ✅ मोबाइल रीसेट करें
            setPassword('')
            setRoles(["USER"]) // ✅ भूमिकाओं को रीसेट करें
            setMembershipTier('NONE'); // ✅ सदस्यता फ़ील्ड्स रीसेट करें
            setMembershipStatus('INACTIVE');
            setIsSponsoredMember(false);
            navigate('/dashboard/users')
        }
    }, [isSuccess, navigate])

    const onUsernameChanged = e => setUsername(e.target.value)
    const onEmailChanged = e => setEmail(e.target.value) // ✅ ईमेल हैंडलर
    const onMobileChanged = e => setMobile(e.target.value) // ✅ मोबाइल हैंडलर
    const onPasswordChanged = e => setPassword(e.target.value)

    const onRolesChanged = e => {
        const values = Array.from(
            e.target.selectedOptions, //HTMLCollection 
            (option) => option.value
        )
        setRoles(values)
    }

    // ✅ प्रायोजित सदस्य स्थिति टॉगल करें
    const onIsSponsoredMemberChanged = () => {
        setIsSponsoredMember(prev => !prev);
        // यदि प्रायोजित किया गया है, तो सदस्यता टियर और स्थिति को अपडेट करें
        if (!isSponsoredMember) { // यदि यह 'true' होने जा रहा है
            setMembershipTier('PREMIUM');
            setMembershipStatus('SPONSORED');
        } else { // यदि यह 'false' होने जा रहा है
            setMembershipTier('NONE');
            setMembershipStatus('INACTIVE');
        }
    };
    
    // ✅ सदस्यता टियर बदलें
    const onMembershipTierChanged = e => {
        const value = e.target.value;
        setMembershipTier(value);
        if (value !== 'NONE' && membershipStatus === 'INACTIVE') {
            setMembershipStatus('ACTIVE'); // यदि कोई टियर चुना गया है और निष्क्रिय है, तो सक्रिय करें
        } else if (value === 'NONE') {
            setMembershipStatus('INACTIVE'); // यदि कोई टियर नहीं चुना गया है, तो निष्क्रिय करें
        }
    };

    // ✅ सदस्यता स्थिति बदलें
    const onMembershipStatusChanged = e => {
        const value = e.target.value;
        setMembershipStatus(value);
        // यदि स्थिति 'SPONSORED' पर सेट की जाती है, तो isSponsoredMember को भी अपडेट करें
        if (value === 'SPONSORED') {
            setIsSponsoredMember(true);
            setMembershipTier('PREMIUM'); // प्रायोजित होने पर टियर को प्रीमियम पर सेट करें
        } else if (value !== 'SPONSORED' && isSponsoredMember) {
            // यदि स्थिति 'SPONSORED' से हटाई जाती है और isSponsoredMember true था
            setIsSponsoredMember(false);
        }
    };


    const canSave = [roles.length, validUsername, validPassword, validEmail, validMobile].every(Boolean) && !isLoading; // ✅ वैलिडेशन अपडेटेड

    const onSaveUserClicked = async (e) => {
        e.preventDefault()
        if (canSave) {
            await addNewUser({ 
                name: username, // ✅ 'username' को 'name' में बदलें
                email, // ✅ ईमेल
                mobile, // ✅ मोबाइल
                password, 
                roles,
                membershipTier, // ✅ सदस्यता टियर
                membershipStatus, // ✅ सदस्यता स्थिति
                isSponsoredMember, // ✅ प्रायोजित सदस्य
             })
        }
    }

    const rolesOptions = Object.values(ROLES).map(role => {
        return (
            <option
                key={role}
                value={role}

            > {role}</option >
        )
    })

    const membershipTierOptions = ["NONE", "BASIC", "PREMIUM"].map(tier => {
        return (
            <option key={tier} value={tier}>
                {tier === 'NONE' ? 'कोई नहीं' : tier === 'BASIC' ? 'बुनियादी' : 'प्रीमियम'}
            </option>
        );
    });

    const membershipStatusOptions = ["ACTIVE", "INACTIVE", "PENDING", "EXPIRED", "CANCELLED", "FREE_TRIAL", "SPONSORED"].map(status => {
        return (
            <option key={status} value={status}>
                {status === 'ACTIVE' ? 'सक्रिय' :
                 status === 'INACTIVE' ? 'निष्क्रिय' :
                 status === 'PENDING' ? 'लंबित' :
                 status === 'EXPIRED' ? 'समाप्त' :
                 status === 'CANCELLED' ? 'रद्द' :
                 status === 'FREE_TRIAL' ? 'मुफ्त परीक्षण' :
                 status === 'SPONSORED' ? 'प्रायोजित' : status}
            </option>
        );
    });

    const errClass = isError ? "errmsg" : "offscreen"
    const validUserClass = !validUsername ? 'form__input--incomplete' : ''
    const validEmailClass = !validEmail ? 'form__input--incomplete' : '' // ✅ नया
    const validMobileClass = !validMobile ? 'form__input--incomplete' : '' // ✅ नया
    const validPwdClass = !validPassword ? 'form__input--incomplete' : ''
    const validRolesClass = !Boolean(roles.length) ? 'form__input--incomplete' : ''


    const content = (
        <>
            <p className={errClass}>{error?.data?.message}</p>

            <Form className="form" onSubmit={onSaveUserClicked}> {/* ✅ form को Form component से बदलें */}
                <div className="form__title-row">
                    <h2>New User</h2>
                    <div className="form__action-buttons">
                        <Button
                            className="icon-button"
                            title="Save"
                            disabled={!canSave}
                            variant="primary"
                        >
                            <FontAwesomeIcon icon={faSave} /> Save {isLoading ? (<Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />) : null}
                        </Button>
                    </div>
                </div>
                <Form.Group className="formGroup">
                    <Form.Label htmlFor="username">
                        Name: <span className="nowrap">[3-20 letters]</span>
                    </Form.Label>
                    <Form.Control
                        className={`${validUserClass}`}
                        id="username"
                        name="username"
                        type="text"
                        autoComplete="off"
                        value={username}
                        onChange={onUsernameChanged}
                    />
                </Form.Group>

                <Form.Group className="formGroup">
                    <Form.Label htmlFor="email">
                        Email:
                    </Form.Label>
                    <Form.Control
                        className={`${validEmailClass}`}
                        id="email"
                        name="email"
                        type="email"
                        autoComplete="off"
                        value={email}
                        onChange={onEmailChanged}
                    />
                </Form.Group>

                <Form.Group className="formGroup">
                    <Form.Label htmlFor="mobile">
                        Mobile:
                    </Form.Label>
                    <Form.Control
                        className={`${validMobileClass}`}
                        id="mobile"
                        name="mobile"
                        type="text"
                        autoComplete="off"
                        value={mobile}
                        onChange={onMobileChanged}
                        maxLength={10}
                    />
                </Form.Group>

                <Form.Group className="formGroup">
                    <Form.Label htmlFor="password">
                        Password: <span className="nowrap">[4-12 chars incl. !@#$%]</span>
                    </Form.Label>
                    <Form.Control
                        className={`${validPwdClass}`}
                        id="password"
                        name="password"
                        type="password"
                        value={password}
                        onChange={onPasswordChanged}
                    />
                </Form.Group>

                <Form.Group className="formGroup">
                    <Form.Label htmlFor="roles">
                        ASSIGNED ROLES:
                    </Form.Label>
                    <Form.Control
                        as="select"
                        multiple
                        id="roles"
                        name="roles"
                        className={`${validRolesClass}`}
                        size="3"
                        value={roles}
                        onChange={onRolesChanged}
                    >
                        {rolesOptions}
                    </Form.Control>
                </Form.Group>

                {/* ✅ नए सदस्यता फ़ील्ड्स */}
                <Form.Group className="formGroup">
                    <Form.Label htmlFor="membershipTier">
                        MEMBERSHIP TIER:
                    </Form.Label>
                    <Form.Control
                        as="select"
                        id="membershipTier"
                        name="membershipTier"
                        value={membershipTier}
                        onChange={onMembershipTierChanged}
                    >
                        {membershipTierOptions}
                    </Form.Control>
                </Form.Group>

                <Form.Group className="formGroup">
                    <Form.Label htmlFor="membershipStatus">
                        MEMBERSHIP STATUS:
                    </Form.Label>
                    <Form.Control
                        as="select"
                        id="membershipStatus"
                        name="membershipStatus"
                        value={membershipStatus}
                        onChange={onMembershipStatusChanged}
                    >
                        {membershipStatusOptions}
                    </Form.Control>
                </Form.Group>

                <Form.Group className="formGroup">
                    <Form.Check
                        type="checkbox"
                        id="isSponsoredMember"
                        label="SPONSORED MEMBER:"
                        checked={isSponsoredMember}
                        onChange={onIsSponsoredMemberChanged}
                        className="form__checkbox-container"
                    />
                </Form.Group>
            </Form>
        </>
    )

    return content
}
export default NewUserForm
